///---------------------------------------------------------------------------
//! Copyright (C) ASQUARED SRL - All Rights Reserved
//* Unauthorized copying of this file, via any medium is strictly prohibited
//* Proprietary and confidential
//* Written by Alexandru Gârbacea <g99.alex@yahoo.com>, September 2022
//? @author g99.alex@yahoo.com
///---------------------------------------------------------------------------

import React, { useState, useEffect, useRef } from 'react'
import { countryCodes, countryNames, EuCountries } from '../../databases/CountryCodes';
import { DeliveryConditions } from '../../databases/DeliveryCondition';
import { TransactionNatures } from '../../databases/TransactionNature';
import { TransportModes } from '../../databases/TransportMode';

import { getHeaders, getHeaderIndex } from '../../services/ExcelService';
import { createNotification } from '../../services/NotificationService';
import { checkValueCodVamal } from '../../services/CodVamalService';
import { validateVATEu } from '../../services/ValidationService';

import './LayoutForm.css'

const LayoutForm = ({fileData, currentIndex, save, toUpdate, updateData, type, discard}) => {

    const formRef = useRef();
    const [headers, setHeaders] = useState([])
    const [req, setReq] = useState(false)
    const [formData, setFormData] = useState({
        codVamal: '',
        codVamalImpl: '',
        valoareFiscala: '',
        valoareFiscalaImpl: 0,
        valoareStatistica: '',
        valoareStatisticaImpl: 0,
        taraOrigine: '',
        taraOrigineImpl: `${countryCodes[0]}`,
        taraExpediere: '',
        taraExpediereImpl: `${EuCountries.Item[0].Code}`,
        greutateNeta: '',
        greutateNetaImpl: 0,
        naturaTranzactiei: '',
        naturaTranzactieiImpl: `${TransactionNatures[0].categ}.${TransactionNatures[0].code}`,
        conditieLivrare: '',
        conditieLivrareImpl: `${DeliveryConditions[0].code}`,
        modTransport: '',
        modTransportImpl: `${TransportModes[2].code}`,
        // req achizitii
        vat: '',
        vatImpl: '',
        // req livrari
        dataFactura: '',
        dataFacturaImpl: '',
        um: '',
        umImpl: 'BUC',
        cantitate: '',
        cantitateImpl: 0,
        cursDeSchimb: '',
        cursDeSchimbImpl: '',
        moneda: '',
        monedaImpl: 'RON',
        valoareValuta: '',
        valoareValutaImpl: 0,
        dataTranzactiei: '',
        dataTranzactieiImpl: '',
    })

    useEffect(() => {
        if(toUpdate) {
            setHeaders(updateData.headers);
            setFormData(updateData.data);
        }
        else {
            setHeaders(getHeaders(fileData.values[currentIndex], true))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileData, currentIndex, toUpdate])

    useEffect(() => {
        if (type === 'livrari') {
            setFormData(prev => ({...prev}))
            setReq(true)
        }
        else {
            setFormData(prev => ({...prev}))
            setReq(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [type])

    useEffect(() => {
      formRef.current && formRef.current.scrollIntoView({ behavior: "smooth", inline: "center" });
    }, [formRef])

    const [autoDetectWarning, setAutoDetectWarning] = useState(false)

    // Normalize text: remove diacritics, lowercase, collapse whitespace
    const normalize = (str) => {
        return str
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9]/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
    }

    // Keywords mapped to each form field (Romanian + English common names)
    const fieldKeywords = {
        codVamal:           ['cod vamal', 'cod nc', 'cod hs', 'hs code', 'commodity code', 'cod taric', 'cod produs', 'cod marfa', 'nomenclatura combinata', 'customs code'],
        valoareFiscala:     ['valoare fiscala', 'val fiscala', 'invoice value', 'fiscal value', 'valoare factura', 'valoare facturata'],
        valoareStatistica:  ['valoare statistica', 'val statistica', 'statistical value', 'val stat'],
        taraOrigine:        ['tara origine', 'tara de origine', 'country of origin', 'origin country', 'cod tara origine', 'tara provenienta'],
        taraExpediere:      ['tara expediere', 'tara destinatie', 'tara expeditie', 'country of dispatch', 'dispatch country', 'tara livrare', 'tara dest'],
        greutateNeta:       ['greutate neta', 'greutate', 'net weight', 'masa neta', 'weight', 'net mass', 'kg net'],
        naturaTranzactiei:  ['natura tranzactiei', 'nat tranzactie', 'transaction nature', 'tip tranzactie', 'natura tranzactie'],
        conditieLivrare:    ['conditie livrare', 'conditii livrare', 'conditii de livrare', 'delivery condition', 'incoterms', 'cond livrare'],
        modTransport:       ['mod transport', 'transport mode', 'tip transport', 'modalitate transport', 'fel transport'],
        vat:                ['vat', 'cif', 'cod fiscal', 'cui', 'tax id', 'vat number', 'cod tva', 'cod identificare fiscala', 'numar tva'],
        dataFactura:        ['data factura', 'data fact', 'invoice date', 'data facturii'],
        um:                 ['unitate masura', 'unitate de masura', 'unit of measure', 'measurement unit', 'u m'],
        cantitate:          ['cantitate', 'quantity', 'qty', 'numar bucati', 'nr buc'],
        cursDeSchimb:       ['curs de schimb', 'curs schimb', 'curs valutar', 'exchange rate', 'rata schimb'],
        moneda:             ['moneda', 'currency', 'valuta', 'deviza', 'cod moneda'],
        valoareValuta:      ['valoare valuta', 'val valuta', 'foreign value', 'valoare in valuta', 'valoare devize'],
        dataTranzactiei:    ['data tranzactiei', 'data tranzactie', 'transaction date', 'data operatiunii'],
    }

    const checkHeaders = () => {
        if (!headers || headers.length === 0) return

        const obj = {}
        const usedHeaders = new Set()

        // Score each field against each header, pick best matches
        const candidates = []
        for (const fieldKey of Object.keys(fieldKeywords)) {
            const keywords = fieldKeywords[fieldKey]
            for (const header of headers) {
                const normalizedHeader = normalize(header)
                let bestScore = 0

                for (const keyword of keywords) {
                    const normalizedKeyword = normalize(keyword)
                    let score = 0

                    if (normalizedHeader === normalizedKeyword ||
                        normalizedHeader.replace(/\d+\s*-\s*/, '') === normalizedKeyword) {
                        score = 100 // Exact match
                    } else if (normalizedHeader.replace(/\d+\s*-\s*/, '').startsWith(normalizedKeyword)) {
                        score = 80 // Header starts with keyword
                    } else if (normalizedHeader.includes(normalizedKeyword)) {
                        score = 60 // Header contains keyword
                    } else {
                        // Check if all words of keyword are present in the header
                        const keywordWords = normalizedKeyword.split(' ')
                        const headerWords = normalizedHeader.split(' ')
                        const matchedWords = keywordWords.filter(w => headerWords.some(hw => hw.includes(w) || w.includes(hw)))
                        if (matchedWords.length === keywordWords.length && keywordWords.length >= 2) {
                            score = 50
                        } else if (matchedWords.length >= 2 && matchedWords.length / keywordWords.length >= 0.7) {
                            score = 30
                        }
                    }

                    // Prefer longer keyword matches (more specific)
                    if (score > 0) {
                        score += normalizedKeyword.length * 0.1
                    }
                    bestScore = Math.max(bestScore, score)
                }

                if (bestScore > 0) {
                    candidates.push({ fieldKey, header, score: bestScore })
                }
            }
        }

        // Sort by score descending, then assign greedily (each header used once, each field once)
        candidates.sort((a, b) => b.score - a.score)
        const assignedFields = new Set()

        for (const { fieldKey, header, score } of candidates) {
            if (assignedFields.has(fieldKey) || usedHeaders.has(header)) continue
            if (score < 30) continue // Minimum threshold
            obj[fieldKey] = getHeaderIndex(header)
            assignedFields.add(fieldKey)
            usedHeaders.add(header)
        }

        if (Object.keys(obj).length > 0) {
            setFormData(prev => ({...prev, ...obj}))
            setAutoDetectWarning(true)
            createNotification('info', `${Object.keys(obj).length} câmpuri au fost detectate automat din antetele Excel.`, 'Auto-detectare', 4000)
        } else {
            createNotification('warning', 'Nu s-au putut detecta câmpuri din antetele Excel. Completați manual.', 'Auto-detectare', 4000)
        }
    }


    const handleSubmit = (e) => {
        e.preventDefault();
        save(formData, headers)
    }

    // Assign values to formData and do not allow same value twice
    const setUniqueFormData = (keyToUpdate, newValue) => {
        let isUnique = true;
        // Loop through formData to check for column to be unique. DO not check with implicit values
        for (const key in formData) {
            if (keyToUpdate.includes("Impl")) break;
            if (key.includes("Impl")) continue;
            if (newValue !== "false" && formData[key] === newValue && key !== keyToUpdate) {
                isUnique = false;
                break;
            }
        }

        if (isUnique) {
            if (!toUpdate && !keyToUpdate.includes("Impl") && newValue !== "false") {
                // Check validity of selected column by verifying against the first value in the column
                const firstRowValues = fileData.values[0][0];
                if (firstRowValues) {
                    const rowKeys = Object.keys(firstRowValues);
                    const valueToCheck = firstRowValues[rowKeys[+newValue]];
                    // Start the check
                    let mightBrWrong = false;
                    switch (keyToUpdate) {
                        case 'codVamal':
                            mightBrWrong = !checkValueCodVamal(valueToCheck);
                            break;
                        case 'valoareFiscala':
                        case 'valoareStatistica':
                        case 'greutateNeta':
                            mightBrWrong = !Number.isSafeInteger(Math.floor(+valueToCheck))
                            break;
                        case 'taraOrigine':
                        case 'taraExpediere':
                            mightBrWrong = !new Set(countryCodes).has(valueToCheck);
                            break;
                        case 'vat':
                            mightBrWrong = !validateVATEu(valueToCheck.toString()) || checkValueCodVamal(valueToCheck); // Throws false positive if COD VAMAL is checked, so we check not to be one
                            break;
                        default:
                            break;
                    }
                    mightBrWrong && createNotification('warning', 'Este posibil ca coloana selectată să nu conțină valori valide, judecând după prima valoare din aceasta ('+valueToCheck+')', "Asigurați-vă", 5000);
                }
            }
            setFormData(prev => ({...prev, [keyToUpdate]: newValue}));
        }
        else createNotification('error', 'Nu puteți asigna aceeași coloană de mai multe ori.');
    }

    const renderHeaderOptions = () => (
        <>
            <option value={false}>Nici o coloană (Din valoarea implicită)</option>
            {headers && headers.length > 0 && headers.map((name, index) => (
                <option key={`${name} - ${index}`} value={getHeaderIndex(name)}>{name}</option>
            ))}
        </>
    )

    return (
        <form className='lf-form' onSubmit={e => handleSubmit(e)} ref={formRef}>

            <div className='lf-form-header'>
                <h2 className='lf-title'>Locație valori în fișierul Excel</h2>
                <p className='lf-subtitle'>Mapați coloanele din fișierul Excel la câmpurile raportului Intrastat</p>
                {!toUpdate && <button type='button' className='button-secondary lf-btn-autodetect' onClick={checkHeaders}>
                    Auto-detectare coloane
                </button>}
            </div>

            {autoDetectWarning && (
                <div className='lf-autodetect-warning'>
                    <span className='lf-autodetect-warning-icon'>&#9888;</span>
                    <div>
                        <strong>Verificați rezultatele!</strong>
                        <p>Auto-detectarea este orientativă. Verificați fiecare câmp înainte de a salva.</p>
                    </div>
                    <button type='button' className='lf-autodetect-warning-close' onClick={() => setAutoDetectWarning(false)}>&times;</button>
                </div>
            )}

            {/* ── Required Fields ──────────────────────────────────────── */}
            <section className='lf-section'>
                <div className='lf-section-header'>
                    <h3 className='lf-section-title lf-section-title--req'>Câmpuri obligatorii</h3>
                    <span className='lf-section-line'></span>
                </div>
                <div className='lf-cards-grid'>

                    {/* Cod vamal */}
                    <div className='lf-card lf-card--required'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Cod vamal (HS)</span>
                            <span className='lf-badge lf-badge--required'>Obligatoriu</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='codVamal'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("codVamal", e.target.value)}
                                    value={formData.codVamal}
                                    name='codVamal' id='codVamal' className='input-text lf-select' required>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='codVamalImpl'>Valoare implicită:</label>
                                <input type='text' value={formData.codVamalImpl}
                                    onChange={(e) => setUniqueFormData("codVamalImpl", e.target.value)}
                                    className='input-text lf-input' title='codVamalImpl' id='codVamalImpl' />
                            </div>
                        </div>
                    </div>

                    {/* Valoare fiscala */}
                    <div className='lf-card lf-card--required'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Valoare fiscală</span>
                            <span className='lf-badge lf-badge--required'>Obligatoriu</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='valoareFiscala'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("valoareFiscala", e.target.value)}
                                    value={formData.valoareFiscala}
                                    name='valoareFiscala' id='valoareFiscala' className='input-text lf-select' required>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='valoareFiscalaImpl'>Valoare implicită:</label>
                                <input type='number' value={formData.valoareFiscalaImpl}
                                    onChange={(e) => setUniqueFormData("valoareFiscalaImpl", e.target.value)}
                                    className='input-text lf-input' title='valoareFiscalaImpl' id='valoareFiscalaImpl' />
                            </div>
                        </div>
                    </div>

                    {/* Valoare statistica */}
                    <div className='lf-card lf-card--required'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Valoare statistică</span>
                            <span className='lf-badge lf-badge--required'>Obligatoriu</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='valoareStatistica'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("valoareStatistica", e.target.value)}
                                    value={formData.valoareStatistica}
                                    name='valoareStatistica' id='valoareStatistica' className='input-text lf-select' required>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='valoareStatisticaImpl'>Valoare implicită:</label>
                                <input type='number' value={formData.valoareStatisticaImpl}
                                    onChange={(e) => setUniqueFormData("valoareStatisticaImpl", e.target.value)}
                                    className='input-text lf-input' title='valoareStatisticaImpl' id='valoareStatisticaImpl' />
                            </div>
                        </div>
                    </div>

                    {/* Tara origine */}
                    <div className='lf-card lf-card--required'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Țară origine</span>
                            <span className='lf-badge lf-badge--required'>Obligatoriu</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='taraOrigine'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("taraOrigine", e.target.value)}
                                    value={formData.taraOrigine}
                                    name='taraOrigine' id='taraOrigine' className='input-text lf-select' required>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='taraOrigineImpl'>Valoare implicită:</label>
                                <select onChange={(e) => setUniqueFormData("taraOrigineImpl", e.target.value)}
                                    value={formData.taraOrigineImpl}
                                    name='taraOrigineImpl' id='taraOrigineImpl' className='input-text lf-select'>
                                    <option value={false}>Nici o valoare</option>
                                    {countryCodes.map((code, index) => (
                                        <option key={`${code} - ${index}`} value={`${code} - ${countryNames[index]}`}>
                                            {`${code} - ${countryNames[index]}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Tara expediere */}
                    <div className='lf-card lf-card--required'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Țară expediere</span>
                            <span className='lf-badge lf-badge--required'>Obligatoriu</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='taraExpediere'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("taraExpediere", e.target.value)}
                                    value={formData.taraExpediere}
                                    name='taraExpediere' id='taraExpediere' className='input-text lf-select' required>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='taraExpediereImpl'>Valoare implicită:</label>
                                <select onChange={(e) => setUniqueFormData("taraExpediereImpl", e.target.value)}
                                    value={formData.taraExpediereImpl}
                                    name='taraExpediereImpl' id='taraExpediereImpl' className='input-text lf-select'>
                                    <option value={false}>Nici o valoare</option>
                                    {EuCountries.Item.map((itm, index) => (
                                        <option key={`${itm.Code} - ${index}`} value={`${itm.Code} - ${itm.NameRo}`}>
                                            {`${itm.Code} - ${itm.NameRo}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Greutate neta */}
                    <div className='lf-card lf-card--required'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Greutate netă</span>
                            <span className='lf-badge lf-badge--required'>Obligatoriu</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='greutateNeta'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("greutateNeta", e.target.value)}
                                    value={formData.greutateNeta}
                                    name='greutateNeta' id='greutateNeta' className='input-text lf-select' required>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='greutateNetaImpl'>Valoare implicită:</label>
                                <input type='number' value={formData.greutateNetaImpl}
                                    onChange={(e) => setUniqueFormData("greutateNetaImpl", e.target.value)}
                                    className='input-text lf-input' title='greutateNetaImpl' id='greutateNetaImpl' />
                            </div>
                        </div>
                    </div>

                    {/* Natura tranzactiei */}
                    <div className='lf-card lf-card--required'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Natura tranzacției</span>
                            <span className='lf-badge lf-badge--required'>Obligatoriu</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='naturaTranzactiei'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("naturaTranzactiei", e.target.value)}
                                    value={formData.naturaTranzactiei}
                                    name='naturaTranzactiei' id='naturaTranzactiei' className='input-text lf-select' required>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='naturaTranzactieiImpl'>Valoare implicită:</label>
                                <select onChange={(e) => setUniqueFormData("naturaTranzactieiImpl", e.target.value)}
                                    value={formData.naturaTranzactieiImpl}
                                    name='naturaTranzactieiImpl' id='naturaTranzactieiImpl' className='input-text lf-select'>
                                    <option value={false}>Nici o valoare</option>
                                    {TransactionNatures.map((item, index) => (
                                        <option key={`${item.value} - ${index}`} value={`${item.categ}.${item.code}`}>
                                            {`${item.categ}.${item.code} - ${item.value}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Conditie livrare */}
                    <div className='lf-card lf-card--required'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Condiție livrare</span>
                            <span className='lf-badge lf-badge--required'>Obligatoriu</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='conditieLivrare'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("conditieLivrare", e.target.value)}
                                    value={formData.conditieLivrare}
                                    name='conditieLivrare' id='conditieLivrare' className='input-text lf-select' required>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='conditieLivrareImpl'>Valoare implicită:</label>
                                <select onChange={(e) => setUniqueFormData("conditieLivrareImpl", e.target.value)}
                                    value={formData.conditieLivrareImpl}
                                    name='conditieLivrareImpl' id='conditieLivrareImpl' className='input-text lf-select'>
                                    <option value={false}>Nici o valoare</option>
                                    {DeliveryConditions.map((item, index) => (
                                        <option key={`${item.code} - ${index}`} value={`${item.code}`}>
                                            {`${item.code} - ${item.value}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* Mod transport */}
                    <div className='lf-card lf-card--required'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Mod transport</span>
                            <span className='lf-badge lf-badge--required'>Obligatoriu</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='modTransport'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("modTransport", e.target.value)}
                                    value={formData.modTransport}
                                    name='modTransport' id='modTransport' className='input-text lf-select' required>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='modTransportImpl'>Valoare implicită:</label>
                                <select onChange={(e) => setUniqueFormData("modTransportImpl", e.target.value)}
                                    value={formData.modTransportImpl}
                                    name='modTransportImpl' id='modTransportImpl' className='input-text lf-select'>
                                    <option value={false}>Nici o valoare</option>
                                    {TransportModes.map((item, index) => (
                                        <option key={`${item.code} - ${index}`} value={`${item.code}`}>
                                            {`${item.code} - ${item.value}`}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* VAT (CIF) — conditionally required */}
                    <div className={`lf-card${req ? ' lf-card--cond-required' : ''}`}>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>VAT (CIF)</span>
                            {req && <span className='lf-badge lf-badge--cond'>Obligatoriu (livrări)</span>}
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='vat'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("vat", e.target.value)}
                                    value={formData.vat} required={req}
                                    name='vat' id='vat' className='input-text lf-select'>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='vatImpl'>Valoare implicită:</label>
                                <input type='text' value={formData.vatImpl}
                                    onChange={(e) => setUniqueFormData("vatImpl", e.target.value)}
                                    className='input-text lf-input' title='vatImpl' id='vatImpl' />
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            {/* ── Optional Fields ───────────────────────────────────────── */}
            <section className='lf-section'>
                <div className='lf-section-header'>
                    <h3 className='lf-section-title'>Câmpuri opționale</h3>
                    <span className='lf-section-line'></span>
                </div>
                <div className='lf-cards-grid'>

                     {/* Valoare valuta */}
                    <div className='lf-card lf-card--bnr'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Valoare valută</span>
                            <span className='lf-badge lf-badge--bnr'>Conversie BNR</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='valoareValuta'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("valoareValuta", e.target.value)}
                                    value={formData.valoareValuta}
                                    name='valoareValuta' id='valoareValuta' className='input-text lf-select'>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='valoareValutaImpl'>Valoare implicită:</label>
                                <input type='number' value={formData.valoareValutaImpl}
                                    onChange={(e) => setUniqueFormData("valoareValutaImpl", e.target.value)}
                                    className='input-text lf-input' title='valoareValutaImpl' id='valoareValutaImpl' />
                            </div>
                        </div>
                        <p className='lf-card-hint'>Necesar pentru conversia automată din valută în RON la cursul BNR.</p>
                    </div>

                    {/* Data tranzactiei */}
                    <div className='lf-card lf-card--bnr'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Data tranzacției</span>
                            <span className='lf-badge lf-badge--bnr'>Conversie BNR</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='dataTranzactiei'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("dataTranzactiei", e.target.value)}
                                    value={formData.dataTranzactiei}
                                    name='dataTranzactiei' id='dataTranzactiei' className='input-text lf-select'>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='dataTranzactieiImpl'>Valoare implicită:</label>
                                <input type='text' value={formData.dataTranzactieiImpl}
                                    onChange={(e) => setUniqueFormData("dataTranzactieiImpl", e.target.value)}
                                    className='input-text lf-input' title='dataTranzactieiImpl' id='dataTranzactieiImpl'
                                    placeholder='zz/ll/aaaa' />
                            </div>
                        </div>
                        <p className='lf-card-hint'>Necesar pentru conversia automată din valută în RON la cursul BNR.</p>
                    </div>

                    {/* Moneda */}
                    <div className='lf-card lf-card--bnr'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Monedă</span>
                            <span className='lf-badge lf-badge--bnr'>Conversie BNR</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='moneda'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("moneda", e.target.value)}
                                    value={formData.moneda}
                                    name='moneda' id='moneda' className='input-text lf-select'>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='monedaImpl'>Valoare implicită:</label>
                                <input type='text' value={formData.monedaImpl}
                                    onChange={(e) => setUniqueFormData("monedaImpl", e.target.value)}
                                    className='input-text lf-input' title='monedaImpl' id='monedaImpl' />
                            </div>
                        </div>
                        <p className='lf-card-hint'>Necesar pentru conversia automată din valută în RON la cursul BNR.</p>
                    </div>

                    {/* Data factura */}
                    <div className='lf-card'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Dată factură</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='dataFactura'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("dataFactura", e.target.value)}
                                    value={formData.dataFactura}
                                    name='dataFactura' id='dataFactura' className='input-text lf-select'>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='dataFacturaImpl'>Valoare implicită:</label>
                                <input type='text' value={formData.dataFacturaImpl}
                                    onChange={(e) => setUniqueFormData("dataFacturaImpl", e.target.value)}
                                    className='input-text lf-input' title='dataFacturaImpl' id='dataFacturaImpl'
                                    placeholder='zz/ll/aaaa' />
                            </div>
                        </div>
                    </div>

                    {/* UM */}
                    <div className='lf-card'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>UM</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='um'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("um", e.target.value)}
                                    value={formData.um}
                                    name='um' id='um' className='input-text lf-select'>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='umImpl'>Valoare implicită:</label>
                                <input type='text' value={formData.umImpl}
                                    onChange={(e) => setUniqueFormData("umImpl", e.target.value)}
                                    className='input-text lf-input' title='umImpl' id='umImpl' />
                            </div>
                        </div>
                    </div>

                    {/* Cantitate */}
                    <div className='lf-card'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Cantitate</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='cantitate'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("cantitate", e.target.value)}
                                    value={formData.cantitate}
                                    name='cantitate' id='cantitate' className='input-text lf-select'>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='cantitateImpl'>Valoare implicită:</label>
                                <input type='number' value={formData.cantitateImpl}
                                    onChange={(e) => setUniqueFormData("cantitateImpl", e.target.value)}
                                    className='input-text lf-input' title='cantitateImpl' id='cantitateImpl' />
                            </div>
                        </div>
                    </div>

                    {/* Curs de schimb */}
                    <div className='lf-card'>
                        <div className='lf-card-header'>
                            <span className='lf-card-label'>Curs de schimb</span>
                        </div>
                        <div className='lf-card-body'>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='cursDeSchimb'>Coloană Excel:</label>
                                <select onChange={(e) => setUniqueFormData("cursDeSchimb", e.target.value)}
                                    value={formData.cursDeSchimb}
                                    name='cursDeSchimb' id='cursDeSchimb' className='input-text lf-select'>
                                    {renderHeaderOptions()}
                                </select>
                            </div>
                            <div className='lf-field-group'>
                                <label className='lf-field-label' htmlFor='cursDeSchimbImpl'>Valoare implicită:</label>
                                <input type='number' value={formData.cursDeSchimbImpl}
                                    onChange={(e) => setUniqueFormData("cursDeSchimbImpl", e.target.value)}
                                    className='input-text lf-input' title='cursDeSchimbImpl' id='cursDeSchimbImpl'
                                    step="0.0001" />
                            </div>
                        </div>
                    </div>

                </div>
            </section>

            {/* ── Actions ───────────────────────────────────────────────── */}
            <div className='lf-actions'>
                <input type='submit' className='button-secondary btn-green lf-btn-submit' value='Salvează' />
                <div onClick={() => discard()} className='button-secondary lf-btn-cancel'>Anulează</div>
            </div>

        </form>
    )
}

export default LayoutForm
